import React, { useEffect, useState, useCallback } from 'react';
import { Card } from 'react-bootstrap';
import { Button, CircularProgress, Switch } from '@material-ui/core';
import { Toast } from 'components/shared/Toaster';
import { getMySecuritySettings, updateMySecuritySettings } from 'actions/client';
import styles from '../profile.module.scss';

/**
 * Two-factor requirement of the logged-in user's own organization (ROX-1878). Backed by the
 * TWO_FACTOR_REQUIRED client feature flag. The endpoints resolve the client from the token,
 * so this tab is only rendered for a client admin: a rox admin manages other clients from
 * Settings > Feature Flags.
 */
const SecurityTab = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [twoFactorRequired, setTwoFactorRequired] = useState(false);
    const [savedValue, setSavedValue] = useState(false);

    const isDirty = twoFactorRequired !== savedValue;

    const fetchSettings = useCallback(() => {
        setLoading(true);
        getMySecuritySettings()
            .then((res) => {
                const value = !!res?.twoFactorRequired;
                setTwoFactorRequired(value);
                setSavedValue(value);
            })
            // request() already surfaced the failure as a toast; swallowing keeps the
            // rejection from bubbling up as an unhandled promise.
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setSaving(true);
        updateMySecuritySettings({ twoFactorRequired })
            .then((res) => {
                const value = !!res?.twoFactorRequired;
                setSavedValue(value);
                setTwoFactorRequired(value);
                Toast({
                    type: 'success',
                    message: value
                        ? 'Two-factor authentication is now required'
                        : 'Two-factor authentication is no longer required'
                });
            })
            .catch(() => {})
            .finally(() => setSaving(false));
    };

    if (loading) {
        return (
            <Card className="p-4 text-center">
                <CircularProgress />
            </Card>
        );
    }

    return (
        <Card>
            <div className={styles.profileContainer}>
                <div className={styles.profileHeader}>
                    <h5>Security</h5>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="d-flex align-items-center mb-2">
                        <Switch
                            color="primary"
                            checked={twoFactorRequired}
                            onChange={(e) => setTwoFactorRequired(e.target.checked)}
                            disabled={saving}
                            inputProps={{ 'aria-label': 'Require two-factor authentication' }}
                        />
                        <span style={{ fontSize: 16 }}>Require two-factor authentication</span>
                    </div>

                    <p className="mb-4" style={{ fontSize: 14, color: 'rgba(0,0,0,0.59)', maxWidth: '46rem' }}>
                        Users sign in with their password and then with a code sent to their email.
                        This applies on each user's next sign-in and does not close sessions that are
                        already open. Users who do not have a password yet will be asked to create one
                        the first time they sign in. You can exempt individual users from the Users tab.
                    </p>

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!isDirty || saving}
                    >
                        {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
                    </Button>
                </form>
            </div>
        </Card>
    );
};

export default SecurityTab;
